import { useCallback, useEffect, useState } from "react";
import { Navbar, fetchWithTimeout, pageStyle, mainContentStyle, cardStyle } from "../App";
import LocationDropdown from "../components/LocationDropdown";
import InternationalPhoneInput from "../components/InternationalPhoneInput";

const API_URL = process.env.REACT_APP_API_URL;
const FEEDBACK_URL =
  process.env.REACT_APP_FEEDBACK_FORM_URL ||
  "https://docs.google.com/forms/d/e/1FAIpQLSczBgyx2dK3dE9zOHriGtw6376tppdUnuL9b6fMIx1dGAhmYw/viewform";

function isGoogleMapsLink(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("google.com/maps") ||
    text.includes("maps.app.goo.gl") ||
    text.includes("goo.gl/maps")
  );
}

function Account() {
  const [authUser, setAuthUser] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [loyaltyOffers, setLoyaltyOffers] = useState([]);
  const [registerLocation, setRegisterLocation] = useState("");
  const [registerCustomLocation, setRegisterCustomLocation] = useState("");
  const [profileLocation, setProfileLocation] = useState("");
  const [profileCustomLocation, setProfileCustomLocation] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [showPasswordEditor, setShowPasswordEditor] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [claimingOfferId, setClaimingOfferId] = useState(null);
  const [claimMessage, setClaimMessage] = useState("");
  const [claimedDiscounts, setClaimedDiscounts] = useState([]);
  const [feedbackClaimLoading, setFeedbackClaimLoading] = useState(false);
  const [feedbackClaimInfo, setFeedbackClaimInfo] = useState(null);
  const [completedOrderTokens, setCompletedOrderTokens] = useState([]);
  const [selectedFeedbackOrderToken, setSelectedFeedbackOrderToken] = useState("");

  const resolveLocationForSubmit = (selectedLocation, customLocation) => {
    if (selectedLocation === "OTHER" || selectedLocation === "MAPS_LINK") return String(customLocation || "").trim();
    return String(selectedLocation || "").trim();
  };

  const hydrateLocationPicker = useCallback((savedLocation, setterSelection, setterCustom) => {
    const clean = String(savedLocation || "").trim();
    if (!clean) {
      setterSelection("");
      setterCustom("");
      return;
    }

    const existsInOptions = locationOptions.some((loc) => loc.name === clean);
    if (existsInOptions) {
      setterSelection(clean);
      setterCustom("");
    } else {
      setterSelection(isGoogleMapsLink(clean) ? "MAPS_LINK" : "OTHER");
      setterCustom(clean);
    }
  }, [locationOptions]);

  const loadLocations = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocationOptions(data);
    } catch (_) {
      setLocationOptions([]);
    }
  }, []);

  const loadOffers = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/offers`);
      const data = await res.json();
      if (Array.isArray(data)) setLoyaltyOffers(data);
    } catch (_) {
      setLoyaltyOffers([]);
    }
  }, []);

  const loadClaims = useCallback(async () => {
    if (!authUser) {
      setClaimedDiscounts([]);
      return;
    }
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/claims?status=claimed`, { credentials: "include" });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setClaimedDiscounts(data);
      else setClaimedDiscounts([]);
    } catch (_) {
      setClaimedDiscounts([]);
    }
  }, [authUser]);

  const loadFeedbackClaimInfo = useCallback(async () => {
    if (!authUser) {
      setFeedbackClaimInfo(null);
      return;
    }
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/feedback/status`, { credentials: "include" });
      const data = await res.json();
      if (res.ok) setFeedbackClaimInfo(data);
      else setFeedbackClaimInfo(null);
    } catch (_) {
      setFeedbackClaimInfo(null);
    }
  }, [authUser]);

  const loadCompletedOrderTokens = useCallback(async () => {
    if (!authUser) {
      setCompletedOrderTokens([]);
      return;
    }

    try {
      const res = await fetchWithTimeout(`${API_URL}/orders/mine`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        setCompletedOrderTokens([]);
        return;
      }

      const tokens = [
        ...new Set(
          data
            .filter((row) => {
              const isCompleted = String(row?.status || "").toLowerCase() === "completed";
              const ownedByCurrentUser = Number(row?.student_user_id || 0) === Number(authUser?.id || 0);
              const rowPhoneDigits = String(row?.phone || "").replace(/\D/g, "");
              const accountPhoneDigits = String(authUser?.phone || "").replace(/\D/g, "");
              const matchesPhone = accountPhoneDigits && rowPhoneDigits === accountPhoneDigits;
              return isCompleted && (ownedByCurrentUser || matchesPhone);
            })
            .map((row) => String(row?.order_token || "").trim().toUpperCase())
            .filter(Boolean)
        ),
      ];
      setCompletedOrderTokens(tokens);
    } catch (_) {
      setCompletedOrderTokens([]);
    }
  }, [authUser]);

  const loadSession = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/auth/me`, { credentials: "include" });
      const data = await res.json();

      if (!res.ok || !data?.authenticated || !data?.user) {
        setAuthUser(null);
        setLoyalty(null);
        return;
      }

      setAuthUser(data.user);
      setLoyalty(data.loyalty || null);
      setAuthEmail(data.user.email || "");
      setAuthName(data.user.name || "");
      setAuthPhone(data.user.phone || "");
      hydrateLocationPicker(data.user.default_location, setProfileLocation, setProfileCustomLocation);
    } catch (_) {
      setAuthUser(null);
      setLoyalty(null);
    }
  }, [hydrateLocationPicker]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  useEffect(() => {
    loadFeedbackClaimInfo();
  }, [loadFeedbackClaimInfo]);

  useEffect(() => {
    loadCompletedOrderTokens();
  }, [loadCompletedOrderTokens]);

  useEffect(() => {
    const claimedTokens = new Set(
      (feedbackClaimInfo?.claimed_order_tokens || []).map((token) => String(token || "").toUpperCase())
    );

    const unclaimedTokens = completedOrderTokens.filter((token) => !claimedTokens.has(String(token).toUpperCase()));

    if (selectedFeedbackOrderToken && completedOrderTokens.includes(selectedFeedbackOrderToken)) {
      return;
    }

    if (unclaimedTokens.length > 0) {
      setSelectedFeedbackOrderToken(unclaimedTokens[0]);
      return;
    }

    if (completedOrderTokens.length > 0) {
      setSelectedFeedbackOrderToken(completedOrderTokens[0]);
      return;
    }

    setSelectedFeedbackOrderToken("");
  }, [completedOrderTokens, feedbackClaimInfo, selectedFeedbackOrderToken]);

  const handleClaimOffer = async (offer) => {
    setAuthError("");
    setClaimMessage("");
    setClaimingOfferId(offer.id);
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/claims`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ offer_id: offer.id }),
      }, 10000);

      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.error || "Could not claim this offer.");
        setClaimingOfferId(null);
        return;
      }

      setLoyalty(data.loyalty || null);
      setClaimMessage(`${offer.name} claimed successfully.`);
      await Promise.allSettled([loadClaims(), loadSession()]);
    } catch (_) {
      setAuthError("Could not claim this offer.");
    } finally {
      setClaimingOfferId(null);
    }
  };

  const handleClaimFeedbackPoints = async () => {
    setAuthError("");
    setClaimMessage("");

    if (!selectedFeedbackOrderToken) {
      setAuthError("No completed order found. Complete an order first to claim feedback points.");
      return;
    }

    setFeedbackClaimLoading(true);
    try {
      const res = await fetchWithTimeout(
        `${API_URL}/loyalty/feedback/claim`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ order_token: selectedFeedbackOrderToken }),
        },
        12000
      );
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.error || "Could not claim feedback points.");
        setFeedbackClaimLoading(false);
        return;
      }

      setLoyalty(data.loyalty || null);
      setClaimMessage("Feedback reward claimed: +3 points.");
      await Promise.allSettled([loadSession(), loadFeedbackClaimInfo(), loadCompletedOrderTokens()]);
    } catch (_) {
      setAuthError("Could not claim feedback points.");
    } finally {
      setFeedbackClaimLoading(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setProfileMessage("");
    setAuthLoading(true);

    try {
      const payload = {
        email: authEmail,
        password: authPassword,
      };

      if (authMode === "register") {
        const finalRegisterLocation = resolveLocationForSubmit(registerLocation, registerCustomLocation);

        if (!finalRegisterLocation) {
          setAuthError("Please choose a location or paste your Google Maps link.");
          setAuthLoading(false);
          return;
        }
        if (registerLocation === "MAPS_LINK" && !isGoogleMapsLink(finalRegisterLocation)) {
          setAuthError("Please paste a valid Google Maps link (google.com/maps or maps.app.goo.gl).");
          setAuthLoading(false);
          return;
        }

        payload.name = authName;
        payload.phone = authPhone;
        payload.default_location = finalRegisterLocation;
      }

      const endpoint = authMode === "register" ? `${API_URL}/auth/register` : `${API_URL}/auth/login`;
      const res = await fetchWithTimeout(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      }, 10000);

      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.error || "Authentication failed.");
        setAuthLoading(false);
        return;
      }

      setAuthUser(data.user || null);
      setLoyalty(data.loyalty || null);
      setAuthName(data.user?.name || "");
      setAuthPhone(data.user?.phone || "");
      hydrateLocationPicker(data.user?.default_location, setProfileLocation, setProfileCustomLocation);
      setAuthEmail(data.user?.email || authEmail);
      setAuthPassword("");
      setAuthLoading(false);
      window.dispatchEvent(new Event("student-auth-changed"));
    } catch (_) {
      setAuthError("Could not reach the server.");
      setAuthLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setAuthError("");
    setProfileMessage("");
    setAuthLoading(true);

    try {
      const finalProfileLocation = resolveLocationForSubmit(profileLocation, profileCustomLocation);
      if (!finalProfileLocation) {
        setAuthError("Please choose your default location or add your Google Maps link.");
        setAuthLoading(false);
        return;
      }
      if (profileLocation === "MAPS_LINK" && !isGoogleMapsLink(finalProfileLocation)) {
        setAuthError("Please paste a valid Google Maps link (google.com/maps or maps.app.goo.gl).");
        setAuthLoading(false);
        return;
      }

      const res = await fetchWithTimeout(`${API_URL}/auth/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: authName,
          phone: authPhone,
          default_location: finalProfileLocation,
        }),
      }, 10000);

      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.error || "Failed to save profile.");
        setAuthLoading(false);
        return;
      }

      setAuthUser(data.user || null);
      setLoyalty(data.loyalty || null);
      hydrateLocationPicker(data.user?.default_location, setProfileLocation, setProfileCustomLocation);
      setProfileMessage("Profile updated successfully.");
      setAuthLoading(false);
    } catch (_) {
      setAuthError("Could not save profile.");
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    setAuthError("");
    setProfileMessage("");
    try {
      await fetchWithTimeout(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (_) {
      // ignore network error and still clear local auth UI
    }

    setAuthUser(null);
    setLoyalty(null);
    setAuthPassword("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordMessage("");
    setPasswordError("");
    window.dispatchEvent(new Event("student-auth-changed"));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError("Please fill all password fields.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setPasswordSaving(true);
    try {
      const res = await fetchWithTimeout(
        `${API_URL}/auth/change-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
          }),
        },
        10000
      );

      const data = await res.json();
      if (!res.ok) {
        setPasswordError(data.error || "Could not update password.");
        setPasswordSaving(false);
        return;
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordMessage(data.message || "Password updated successfully.");
    } catch (_) {
      setPasswordError("Could not update password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotMessage("");

    if (!authEmail || !forgotPhone || !forgotNewPassword || !forgotConfirmPassword) {
      setForgotError("Please fill email, phone, and new password fields.");
      return;
    }
    if (forgotNewPassword.length < 6) {
      setForgotError("New password must be at least 6 characters.");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError("New passwords do not match.");
      return;
    }

    setForgotLoading(true);
    try {
      const res = await fetchWithTimeout(
        `${API_URL}/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: authEmail,
            phone: forgotPhone,
            new_password: forgotNewPassword,
          }),
        },
        10000
      );

      const data = await res.json();
      if (!res.ok) {
        setForgotError(data.error || "Could not reset password.");
        setForgotLoading(false);
        return;
      }

      setForgotMessage(data.message || "Password reset successful.");
      setAuthPassword("");
      setForgotPhone("");
      setForgotNewPassword("");
      setForgotConfirmPassword("");
      setShowForgotPassword(false);
      setAuthMode("login");
    } catch (_) {
      setForgotError("Could not reset password.");
    } finally {
      setForgotLoading(false);
    }
  };

  const claimedOrderTokenSet = new Set(
    (feedbackClaimInfo?.claimed_order_tokens || []).map((token) => String(token || "").trim().toUpperCase())
  );
  const selectedFeedbackAlreadyClaimed =
    selectedFeedbackOrderToken && claimedOrderTokenSet.has(String(selectedFeedbackOrderToken).toUpperCase());

  return (
    <div className="mama-page" style={pageStyle}>
      <Navbar />
      <div className="mama-main" style={mainContentStyle}>
        <div className="mama-card" style={cardStyle}>
          <h1 className="mama-heading" style={{ marginTop: 0 }}>Account</h1>

          {authUser ? (
            <>
              <p className="mama-subheading" style={{ marginTop: "-0.3rem" }}>
                Welcome, {authUser.name}. Manage your saved details and rewards.
              </p>

              <div
                style={{
                  background: "var(--beige)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px",
                  padding: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                <p style={{ margin: 0, fontWeight: 700, color: "var(--text-main)" }}>
                  🌟 {loyalty?.points || 0} Loyalty Points
                </p>
                <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                  Tier: {(loyalty?.tier_emoji ? `${loyalty.tier_emoji} ` : "") + (loyalty?.tier || "Starter")}
                  {loyalty?.points_to_next_tier > 0
                    ? ` • ${loyalty.points_to_next_tier} points to next tier`
                    : " • Top tier unlocked"}
                </p>
                <div style={{ marginTop: "0.55rem", background: "#fff", borderRadius: "999px", height: "10px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.max(0, Math.min(100, Number(loyalty?.tier_progress_pct || 0)))}%`,
                      background: "linear-gradient(90deg, #F59E0B, #F97316)",
                    }}
                  />
                </div>
                <p style={{ margin: "0.45rem 0 0", fontSize: "0.8rem", color: "var(--brown-mid)", fontWeight: 700 }}>
                  🔥 Current streak: {Number(loyalty?.current_streak || 0)} week{Number(loyalty?.current_streak || 0) === 1 ? "" : "s"}
                  {Number(loyalty?.streak_bonus_next_order || 0) > 0
                    ? ` • Next order streak bonus: +${Number(loyalty?.streak_bonus_next_order || 0)} pts`
                    : " • Order weekly to trigger streak bonus"}
                </p>
                <p style={{ margin: "0.2rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Best streak: {Number(loyalty?.best_streak || 0)} week{Number(loyalty?.best_streak || 0) === 1 ? "" : "s"}
                  {` • Active weeks in last 8 weeks: ${Number(loyalty?.active_weeks_last_8 || 0)}`}
                </p>
                {loyalty?.next_offer_name && (
                  <>
                    <p style={{ margin: "0.45rem 0 0", fontSize: "0.8rem", color: "var(--text-main)", fontWeight: 700 }}>
                      🎯 Next reward: {loyalty.next_offer_name}
                      {` • ${Number(loyalty?.points_to_next_offer || 0)} pts to unlock`}
                    </p>
                    <div style={{ marginTop: "0.35rem", background: "#fff", borderRadius: "999px", height: "8px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.max(0, Math.min(100, Number(loyalty?.next_offer_progress_pct || 0)))}%`,
                          background: "linear-gradient(90deg, #10B981, #059669)",
                        }}
                      />
                    </div>
                  </>
                )}
                {loyalty?.promotions?.length > 0 && (
                  <ul style={{ margin: "0.55rem 0 0", paddingLeft: "1rem", color: "var(--success)", fontSize: "0.82rem" }}>
                    {loyalty.promotions.map((promo) => (
                      <li key={promo}>{promo}</li>
                    ))}
                  </ul>
                )}
                {!authUser?.welcome_offer_used && (
                  <p style={{ margin: "0.55rem 0 0", fontSize: "0.82rem", color: "var(--orange)" }}>
                    Welcome offer: RM3 off your first order will be applied automatically.
                  </p>
                )}
              </div>

              {loyaltyOffers.length > 0 && (
                <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "12px", padding: "0.9rem 1rem", marginBottom: "1rem" }}>
                  <h2 style={{ margin: 0, fontSize: "0.92rem", color: "var(--text-main)" }}>Available offers</h2>
                  <div style={{ display: "grid", gap: "0.6rem", marginTop: "0.7rem" }}>
                    {loyaltyOffers.map((offer) => {
                      const eligible = (loyalty?.points || 0) >= Number(offer.min_points || 0);
                      return (
                        <div key={offer.id} style={{ background: eligible ? "#EDF5EE" : "#FEF4E4", borderRadius: "10px", padding: "0.7rem 0.8rem", border: `1px solid ${eligible ? "rgba(76, 175, 80, 0.18)" : "rgba(224, 123, 57, 0.18)"}` }}>
                          <strong style={{ display: "block", fontSize: "0.88rem", color: "var(--text-main)" }}>{offer.name}</strong>
                          <span style={{ display: "block", fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
                            RM {Number(offer.discount_amount || 0).toFixed(2)} off · needs {offer.min_points || 0} points
                          </span>
                          {offer.description && (
                            <span style={{ display: "block", fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
                              {offer.description}
                            </span>
                          )}
                          <span style={{ display: "inline-block", marginTop: "0.35rem", fontSize: "0.74rem", fontWeight: 700, color: eligible ? "var(--success)" : "var(--orange)" }}>
                            {eligible ? "Eligible now" : "Keep ordering to unlock"}
                          </span>
                          {eligible && (
                            <div style={{ marginTop: "0.45rem" }}>
                              <button
                                type="button"
                                className="mama-btn-primary"
                                style={{ fontSize: "0.78rem", padding: "0.4rem 0.65rem" }}
                                disabled={claimingOfferId === offer.id}
                                onClick={() => handleClaimOffer(offer)}
                              >
                                {claimingOfferId === offer.id ? "Claiming..." : "Claim reward"}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {claimedDiscounts.length > 0 && (
                <div style={{ background: "#EDF5EE", border: "1px solid rgba(76,175,80,0.2)", borderRadius: "12px", padding: "0.8rem 1rem", marginBottom: "1rem" }}>
                  <h2 style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-main)" }}>Claimed rewards ready for checkout</h2>
                  <div style={{ display: "grid", gap: "0.45rem", marginTop: "0.6rem" }}>
                    {claimedDiscounts.map((claim) => (
                      <div key={claim.id} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", fontSize: "0.82rem", color: "var(--text-main)" }}>
                        <span>{claim.offer_name || "Loyalty reward"}</span>
                        <strong style={{ color: "var(--success)" }}>RM {Number(claim.discount_amount || 0).toFixed(2)} off</strong>
                      </div>
                    ))}
                  </div>
                  <p style={{ margin: "0.6rem 0 0", fontSize: "0.76rem", color: "var(--text-muted)" }}>
                    You can apply multiple claimed rewards when placing an order.
                  </p>
                </div>
              )}

              <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "12px", padding: "0.85rem 1rem", marginBottom: "1rem" }}>
                <h2 style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-main)" }}>Google Form feedback reward</h2>
                <p style={{ margin: "0.45rem 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  Same flow as order tracking: open Google Form, submit feedback, then claim +3 points for your completed order token.
                </p>
                {completedOrderTokens.length > 0 ? (
                  <>
                    <label className="mama-label" style={{ marginTop: "0.6rem" }}>Completed order token</label>
                    <select
                      value={selectedFeedbackOrderToken}
                      onChange={(e) => setSelectedFeedbackOrderToken(e.target.value)}
                      className="mama-input"
                      style={{ marginBottom: "0.45rem" }}
                    >
                      {completedOrderTokens.map((token) => {
                        const claimed = (feedbackClaimInfo?.claimed_order_tokens || []).includes(token);
                        return (
                          <option key={token} value={token}>
                            {token}{claimed ? " (claimed)" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </>
                ) : (
                  <p style={{ margin: "0.55rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    You need at least one completed order before claiming feedback points.
                  </p>
                )}
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.6rem" }}>
                  <a
                    href={FEEDBACK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mama-btn-secondary"
                    style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                  >
                    Leave Feedback →
                  </a>
                  <button
                    type="button"
                    className="mama-btn-primary"
                    onClick={handleClaimFeedbackPoints}
                    disabled={feedbackClaimLoading || !selectedFeedbackOrderToken || selectedFeedbackAlreadyClaimed}
                    style={{ fontSize: "0.8rem", padding: "0.45rem 0.75rem" }}
                  >
                    {feedbackClaimLoading
                      ? "Checking feedback..."
                      : selectedFeedbackAlreadyClaimed
                      ? "Already claimed for this order"
                      : "Claim +3 feedback points"}
                  </button>
                </div>
                {feedbackClaimInfo && (
                  <p style={{ margin: "0.55rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    Claimed feedback rewards: {Number(feedbackClaimInfo.claimed_count || 0)}
                  </p>
                )}
              </div>

              <form onSubmit={handleProfileSave}>
                <label className="mama-label">Email</label>
                <input value={authEmail} className="mama-input" disabled />

                <label className="mama-label">Name</label>
                <input value={authName} onChange={(e) => setAuthName(e.target.value)} className="mama-input" />

                <label className="mama-label">Phone</label>
                <InternationalPhoneInput
                  id="account-profile-phone"
                  value={authPhone}
                  onChange={setAuthPhone}
                  placeholder="Enter your number"
                />
                <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", margin: "-0.45rem 0 0.85rem" }}>
                  Pick country code, then enter number without leading 0.
                </p>

                <label className="mama-label">Default Location</label>
                <LocationDropdown
                  id="account-profile-location"
                  value={profileLocation}
                  onChange={setProfileLocation}
                  locations={locationOptions}
                  mapsLinkLabel="📍 Paste Google Maps link (best for outside service area)"
                  otherLabel="🏠 Type full address manually"
                />
                {(profileLocation === "OTHER" || profileLocation === "MAPS_LINK") && (
                  <>
                    <input
                      value={profileCustomLocation}
                      onChange={(e) => setProfileCustomLocation(e.target.value)}
                      className="mama-input"
                      placeholder={profileLocation === "MAPS_LINK" ? "Paste your Google Maps link" : "Type your full address"}
                      style={{ marginTop: "0.75rem" }}
                    />
                    {profileLocation === "MAPS_LINK" ? (
                      <p style={{ fontSize: "0.82rem", color: "var(--brown-mid)", margin: "0.25rem 0 1rem", fontWeight: 700 }}>
                        Use a full Google Maps link so delivery is exact.
                      </p>
                    ) : (
                      <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0.25rem 0 1rem" }}>
                        This address will be auto-used during checkout.
                      </p>
                    )}
                  </>
                )}

                {authError && <p className="mama-error">{authError}</p>}
                {claimMessage && (
                  <p style={{ color: "var(--success)", marginTop: "0.5rem", fontSize: "0.84rem", fontWeight: 600 }}>
                    {claimMessage}
                  </p>
                )}
                {profileMessage && (
                  <p style={{ color: "var(--success)", marginTop: "0.5rem", fontSize: "0.88rem", fontWeight: 600 }}>
                    {profileMessage}
                  </p>
                )}

                <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                  <button type="submit" className="mama-btn-primary" disabled={authLoading}>
                    {authLoading ? "Saving..." : "Save Profile"}
                  </button>
                  <button
                    type="button"
                    className="mama-btn-secondary"
                    onClick={() => {
                      setShowPasswordEditor((prev) => !prev);
                      setPasswordError("");
                      setPasswordMessage("");
                    }}
                  >
                    {showPasswordEditor ? "Hide Password Editor" : "Edit Password"}
                  </button>
                  <button type="button" className="mama-btn-secondary" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </form>

              {showPasswordEditor && (
                <div
                  style={{
                    marginTop: "0.9rem",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "0.85rem 1rem",
                    background: "#FFF8ED",
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-main)" }}>Update password</h2>
                  <form onSubmit={handleChangePassword} style={{ marginTop: "0.7rem" }}>
                    <label className="mama-label">Current password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mama-input"
                      placeholder="Current password"
                      style={{ marginBottom: "0.55rem" }}
                    />

                    <label className="mama-label">New password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mama-input"
                      placeholder="New password"
                      style={{ marginBottom: "0.55rem" }}
                    />

                    <label className="mama-label">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="mama-input"
                      placeholder="Confirm new password"
                      style={{ marginBottom: "0.6rem" }}
                    />

                    {passwordError && <p className="mama-error" style={{ marginTop: 0 }}>{passwordError}</p>}
                    {passwordMessage && (
                      <p style={{ color: "var(--success)", marginTop: "0.25rem", fontSize: "0.84rem", fontWeight: 600 }}>
                        {passwordMessage}
                      </p>
                    )}

                    <button type="submit" className="mama-btn-primary" disabled={passwordSaving}>
                      {passwordSaving ? "Updating..." : "Save New Password"}
                    </button>
                  </form>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="mama-subheading" style={{ marginTop: "-0.2rem" }}>
                Sign in or create an account to unlock faster checkout and loyalty rewards.
              </p>

              {authMode === "register" && (
                <div
                  style={{
                    background: "var(--beige)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "0.75rem",
                    marginBottom: "0.9rem",
                    fontSize: "0.82rem",
                    color: "var(--text-main)",
                  }}
                >
                  <strong>To create your account, fill:</strong> email, password, full name, WhatsApp number, and default delivery location.
                </div>
              )}

              <form onSubmit={handleAuthSubmit}>
                <label className="mama-label">Email</label>
                <input
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  className="mama-input"
                  placeholder="Email"
                  style={{ marginBottom: "0.55rem" }}
                />

                <label className="mama-label">Password</label>
                <input
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  className="mama-input"
                  placeholder="Password"
                  style={{ marginBottom: authMode === "register" ? "0.55rem" : "0.7rem" }}
                />

                {authMode === "register" && (
                  <>
                    <label className="mama-label">Full Name</label>
                    <input
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      className="mama-input"
                      placeholder="Full name"
                      style={{ marginBottom: "0.55rem" }}
                    />

                    <label className="mama-label">WhatsApp Number</label>
                    <InternationalPhoneInput
                      id="account-register-phone"
                      value={authPhone}
                      onChange={setAuthPhone}
                      placeholder="Enter your number"
                    />
                    <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", margin: "-0.45rem 0 0.75rem" }}>
                      Default country is Malaysia (+60). Enter number without leading 0.
                    </p>

                    <label className="mama-label">Default Delivery Location</label>
                    <LocationDropdown
                      id="account-register-location"
                      value={registerLocation}
                      onChange={setRegisterLocation}
                      locations={locationOptions}
                      mapsLinkLabel="📍 Paste Google Maps link (best for outside service area)"
                      otherLabel="🏠 Type full address manually"
                    />
                    {(registerLocation === "OTHER" || registerLocation === "MAPS_LINK") && (
                      <>
                        <input
                          value={registerCustomLocation}
                          onChange={(e) => setRegisterCustomLocation(e.target.value)}
                          className="mama-input"
                          placeholder={registerLocation === "MAPS_LINK" ? "Paste your Google Maps link" : "Type your full address"}
                          style={{ marginTop: "0.75rem", marginBottom: "0.55rem" }}
                        />
                        <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "-0.2rem 0 0.7rem" }}>
                          {registerLocation === "MAPS_LINK"
                            ? "Paste link only (google.com/maps or maps.app.goo.gl)."
                            : "Write full address details for accurate delivery."}
                        </p>
                      </>
                    )}
                  </>
                )}

                {authError && <p className="mama-error" style={{ marginTop: 0 }}>{authError}</p>}

                <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
                  <button type="submit" className="mama-btn-primary" disabled={authLoading}>
                    {authLoading
                      ? "Please wait..."
                      : authMode === "register"
                      ? "Create Account"
                      : "Login"}
                  </button>
                  <button
                    type="button"
                    className="mama-btn-secondary"
                    onClick={() => {
                      setAuthMode((prev) => (prev === "register" ? "login" : "register"));
                      setAuthError("");
                      setForgotError("");
                      setForgotMessage("");
                      setShowForgotPassword(false);
                    }}
                  >
                    {authMode === "register" ? "Have an account? Login" : "New here? Register"}
                  </button>
                </div>
              </form>

              {authMode === "login" && (
                <div
                  style={{
                    marginTop: "0.8rem",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "0.75rem 0.85rem",
                    background: "#FFF8ED",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Forgot your password?</span>
                    <button
                      type="button"
                      className="mama-btn-secondary"
                      style={{ fontSize: "0.78rem", padding: "0.4rem 0.65rem" }}
                      onClick={() => {
                        setShowForgotPassword((prev) => !prev);
                        setForgotError("");
                        setForgotMessage("");
                      }}
                    >
                      {showForgotPassword ? "Hide" : "Reset Password"}
                    </button>
                  </div>

                  {showForgotPassword && (
                    <form onSubmit={handleForgotPassword} style={{ marginTop: "0.7rem" }}>
                      <label className="mama-label">Account email</label>
                      <input
                        value={authEmail}
                        onChange={(e) => setAuthEmail(e.target.value)}
                        className="mama-input"
                        placeholder="Email"
                        style={{ marginBottom: "0.55rem" }}
                      />

                      <label className="mama-label">WhatsApp number</label>
                      <InternationalPhoneInput
                        id="account-forgot-phone"
                        value={forgotPhone}
                        onChange={setForgotPhone}
                        placeholder="Enter your number"
                      />

                      <label className="mama-label">New password</label>
                      <input
                        type="password"
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        className="mama-input"
                        placeholder="New password"
                        style={{ marginBottom: "0.55rem" }}
                      />

                      <label className="mama-label">Confirm new password</label>
                      <input
                        type="password"
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        className="mama-input"
                        placeholder="Confirm new password"
                        style={{ marginBottom: "0.6rem" }}
                      />

                      {forgotError && <p className="mama-error" style={{ marginTop: 0 }}>{forgotError}</p>}
                      {forgotMessage && (
                        <p style={{ color: "var(--success)", marginTop: "0.25rem", fontSize: "0.84rem", fontWeight: 600 }}>
                          {forgotMessage}
                        </p>
                      )}

                      <button type="submit" className="mama-btn-primary" disabled={forgotLoading}>
                        {forgotLoading ? "Resetting..." : "Reset Password"}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Account;
